import Storage from '@common/services/storage';
import {
  getDefaultPromotions,
  parsePromotionsResponse,
  usesLocalPromotionsSource,
} from '@common/utils/promotions';
import {
  PROMOTIONS_DISMISS,
  PROMOTIONS_DISMISSED_STORAGE_KEY,
  PROMOTIONS_SET,
  type PromotionEntry,
} from '@common/types/promotions';

export { PROMOTIONS_SET, PROMOTIONS_DISMISS };

const PROMOTIONS_FETCH_TIMEOUT_MS = 5_000;

export function setPromotions(items: PromotionEntry[]) {
  return {
    type: PROMOTIONS_SET,
    items,
  } as const;
}

export function dismissPromotion(key: string) {
  return (
    dispatch: (action: { type: typeof PROMOTIONS_DISMISS; key: string }) => void,
    getState: () => { promotions: { dismissedKeys: string[] } },
  ) => {
    if (getState().promotions.dismissedKeys.includes(key)) return;

    dispatch({
      type: PROMOTIONS_DISMISS,
      key,
    });

    persistDismissedKeys(getState().promotions.dismissedKeys);
  };
}

function persistDismissedKeys(keys: string[]) {
  Storage.setObject(PROMOTIONS_DISMISSED_STORAGE_KEY, keys);
}

export async function fetchPromotions(dispatch: (action: ReturnType<typeof setPromotions>) => void) {
  if (usesLocalPromotionsSource()) {
    dispatch(setPromotions(getDefaultPromotions()));
    return;
  }

  const badgesUrl = process.env.BADGES_URL as string;
  const abortController = new AbortController();
  const timeoutId = window.setTimeout(
    () => abortController.abort(),
    PROMOTIONS_FETCH_TIMEOUT_MS,
  );

  try {
    const response = await fetch(badgesUrl, {
      method: 'GET',
      cache: 'no-cache',
      signal: abortController.signal,
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      dispatch(setPromotions([]));
      return;
    }

    const data = await response.json();
    dispatch(setPromotions(parsePromotionsResponse(data)));
  } catch {
    dispatch(setPromotions([]));
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export function hydratePromotionsDismissals() {
  const stored = Storage.getObject(PROMOTIONS_DISMISSED_STORAGE_KEY);
  if (!Array.isArray(stored)) return [];

  return stored.filter((key): key is string => typeof key === 'string');
}
